// Before everything, modules and stylesheets alike: it records whether this is a fresh install while
// localStorage is still empty (src/bootInstallOrigin.js says why it has to be the first import).
import './bootInstallOrigin';
// First, before any other stylesheet: it declares the cascade-layer order that every later
// rule joins, PrimeVue's included. See the file for what each layer holds.
import './ui/cascade.css';
// Then the tokens every component's styles read (colours, z-index). For custom properties the
// order does not matter — they resolve when styles are computed; it sits here next to its sibling.
import './ui/tokens.css';
// The Bronze palette (proposal): repoints the system's tokens. After tokens.css on purpose: see its header.
import './ui/colors.css';
// The system's shared classes (tag, tile, toast, confirmation): every screen draws them, the lane too.
import './ui/system.css';
import {createApp} from 'vue'
import {Amplify} from 'aws-amplify';
import App from './App.vue'
import '@aws-amplify/ui-vue';
import awsmobile from './aws-exports';
import 'primeicons/primeicons.css';
import {generateClient} from 'aws-amplify/api';
import { initializeClient } from './graphql'
import posthog from 'posthog-js';
import Logger from './logger';
import packageInfo from '../package.json';
import { createI18n } from 'vue-i18n'
import messages from './i18n-messages'
import { parseAmplifyConfig } from "aws-amplify/utils";
import { validateRestConfig } from './restApiFallback';
import { installUi } from './ui/install';
import { syncPaletteFlag } from './ui/palette';
import { syncLaneFlag } from './ui/lane';
import { liveDemoOn } from './liveDemo/flag';

// Pick the best-matching supported locale from the browser settings.
// Used only for the pre-login UI (login screen): until the user logs in we don't
// know the restaurant's configured language, so we follow the browser language.
// After login, App.vue overrides the locale with restaurant.clientByClient.language.
function detectBrowserLocale(supportedLocales, fallback = 'es') {
    const candidates = (typeof navigator !== 'undefined' && navigator.languages && navigator.languages.length)
        ? navigator.languages
        : [(typeof navigator !== 'undefined' && navigator.language) || fallback];
    for (const lang of candidates) {
        if (!lang) continue;
        const base = String(lang).toLowerCase().split('-')[0]; // 'fr-FR' -> 'fr'
        if (supportedLocales.includes(base)) return base;
    }
    return fallback;
}

// Event names that are known noise regardless of level — dropped client-side so they
// never reach PostHog. Keep this list tiny: reclassifying at the call site is always
// preferable; this exists only as a belt-and-braces guard.
const NOISE_EVENT_PATTERNS = [
    /^Error fetching changelog/i,
];

// Fires `app_updated` once per version change (old → new), using localStorage as the
// per-device version memory. This makes release adoption measurable and stale PWA
// clients (tablets whose service worker never updated) visible without investigation.
function trackAppUpdate() {
    try {
        const current = String(packageInfo.versionInternal);
        const previous = localStorage.getItem('bronze_app_version');
        if (previous && previous !== current) {
            posthog.capture('app_updated', { fromVersion: previous, toVersion: current });
        }
        localStorage.setItem('bronze_app_version', current);
    } catch (_) { /* never let telemetry throw */ }
}

// Route uncaught errors through the shared Logger (throttle + Error Tracking), so a
// render-loop crash groups by fingerprint instead of flooding, and nothing uncaught
// stays invisible. The Logger dedupes, so these can't spam.
function installGlobalErrorHandlers(app) {
    const globalLogger = new Logger({ screen: 'Global' });
    app.config.errorHandler = (error, instance, info) => {
        globalLogger.error('Unhandled Vue error', { error, componentInfo: info });
        if (process.env.NODE_ENV === 'development') {
            console.error(error);
        }
    };
    window.addEventListener('unhandledrejection', (event) => {
        const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
        globalLogger.error('Unhandled promise rejection', { error });
    });
}

// Async initialization function to ensure proper order
async function initializeApp() {
    try {
        // In demo mode we never touch a real backend: skip Amplify config and the
        // GraphQL client entirely (the demo session serves only mock data).
        if (!__BRONZE_DEMO__) {
            // 1. Configure Amplify first with error handling
            const amplifyConfig = parseAmplifyConfig(awsmobile);
            Amplify.configure(
                {
                    ...amplifyConfig,
                    API: {
                        ...amplifyConfig.API,
                        REST: awsmobile.API.REST,
                    },
                },
                {
                    API: {
                        REST: {
                            retryStrategy: {
                                strategy: 'no-retry' // Overrides default retry strategy
                            },
                        }
                    },
                }
            );

            // 2. Wait a tick to ensure Amplify configuration is fully applied
            await new Promise(resolve => setTimeout(resolve, 0));

            // 2.5 Validate REST config is accessible (diagnostic for InvalidApiName issues)
            validateRestConfig();

            // 3. Create GraphQL client AFTER Amplify is configured
            const client = generateClient();
            if (liveDemoOn()) {
                // Live sales demo on: a GraphQL call that names a demo order resolves with a stub (src/liveDemo/live.js).
                // Read when the page loads (Settings reloads on every change), so only a demo page pays for the module.
                const { wrapGraphqlClient } = await import('./liveDemo/live');
                initializeClient(wrapGraphqlClient(client));
            } else {
                initializeClient(client);
            }
        }

        // 4. Create i18n instance
        // Pre-login: follow the browser language (overridden post-login by App.vue)
        const initialLocale = detectBrowserLocale(Object.keys(messages));
        // Keep the document language in sync with the active locale (accessibility)
        if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('lang', initialLocale);
        }
        const i18n = createI18n({
            legacy: false, // Use Composition API mode (required for vue-i18n v11+)
            locale: initialLocale,
            fallbackLocale: 'es',
            messages,
            globalInjection: true // Enable global $t in templates
        });

        // 5. Create Vue app and configure
        const app = createApp(App);
        app.use(i18n);
        // After i18n: PrimeVue's own strings are fed from it, and follow it when the
        // restaurant's language arrives after login.
        installUi(app, i18n);
        installGlobalErrorHandlers(app);

        // 6. Initialize PostHog after app is set up. Disabled in demo mode so no
        //    telemetry is ever emitted from fake data — a no-op stub is used instead.
        if (__BRONZE_DEMO__) {
            app.config.globalProperties.$posthog = {
                capture: () => {},
                identify: () => {},
                setPersonProperties: () => {},
                setPersonPropertiesForFlags: () => {}
            };
        } else {
            try {
                posthog.init('phc_ulZeci9y9aeinn1GTuZwlC6NZUgEqpJODQTWhb8lFeM', {
                    api_host: 'https://eu.i.posthog.com',
                    person_profiles: 'identified_only',
                    project_id: '27533',
                    enable_recording_console_log: true,
                    capture_pageview: false,
                    capture_pageleave: false,
                    // Autocaptured clicks on a kitchen tablet measure nothing useful; product
                    // usage is tracked through the small set of explicit events instead.
                    autocapture: false,
                    // posthog-js >= 1.356 re-fetches feature flags every 5 minutes while the tab is
                    // visible, to keep "long-running sessions" fresh. Our tablets ARE a long-running
                    // session — a permanently-open kitchen kiosk — so every device burned ~12 billed
                    // flag requests per hour (1.77M in July, the first overage we've ever paid for).
                    // Our flags are near-static pilot toggles read from cache via isFeatureEnabled,
                    // and they still load on init and on login (identify + setPersonPropertiesForFlags),
                    // so a 6h refresh is plenty to eventually pick up a flip without touching the
                    // tablet. Use 0 to disable the periodic refresh entirely. Undocumented option:
                    // re-check it whenever posthog-js is bumped (pinned exactly in package.json).
                    remote_config_refresh_interval_ms: 6 * 60 * 60 * 1000,
                    // Last-resort kill switch for event names we know are pure noise. This only
                    // protects the CURRENT app version — the real fixes are at the call sites.
                    before_send: (event) => {
                        if (event && NOISE_EVENT_PATTERNS.some((re) => re.test(event.event))) {
                            return null;
                        }
                        return event;
                    }
                });

                // Stamp every event (including $exception) with the app version, so stale
                // PWA clients running an old service worker are identifiable in one query.
                posthog.register({ app_version: packageInfo.versionInternal });

                const deviceId = localStorage.getItem('device_id');
                if (deviceId) {
                    posthog.identify(deviceId);
                }

                trackAppUpdate();

                if (liveDemoOn()) {
                    // Live sales demo on: PostHog sends nothing from this page — no event, no exception, no
                    // session recording of the demo screens. Every capture in the app (Logger included) goes through
                    // these two methods, so silencing them here is the single entry point. The flag is read when the
                    // page loads (Settings reloads on every change), so turning the demo off restores them.
                    posthog.capture = () => undefined;
                    posthog.captureException = () => undefined;
                    try { posthog.stopSessionRecording(); } catch (_) { /* no recorder: nothing to stop */ }
                }

                app.config.globalProperties.$posthog = posthog;

                // The store's palette for the next boot, and for this page if it changed. On its
                // own: a failure here must not send PostHog, already initialised, to the stub below.
                try {
                    syncPaletteFlag(posthog);
                } catch (error) {
                    console.error('Palette flag sync failed:', error);
                }
                // The store's service screen, stored for the next boot (never applied under the finger).
                try {
                    syncLaneFlag(posthog);
                } catch (error) {
                    console.error('Lane flag sync failed:', error);
                }
            } catch (error) {
                console.error('PostHog initialization failed:', error);
                // Provide a fallback posthog object to prevent crashes
                app.config.globalProperties.$posthog = {
                    capture: () => {},
                    identify: () => {},
                    setPersonProperties: () => {},
                    setPersonPropertiesForFlags: () => {}
                };
            }
        }

        // 7. Mount the app
        if (__BRONZE_DEMO__) {
            // Mark the document before the first frame so the screenshot engine can
            // assert we are on fixtures, not on a client's session.
            const { markDemoDocument } = await import('./demo/mark');
            markDemoDocument();
        }
        app.mount('#app');
        // DM Sans's italic is the face an order's item comments draw with (OrderItem, the tickets), and
        // nothing asks for it until the first comment shows: fetched now, so that comment never draws in
        // the device's italic and then swaps. After the mount, so it does not compete with the first screen.
        try {
            if (document.fonts && document.fonts.load) document.fonts.load('italic 400 16px "DM Sans"').catch(() => {});
        } catch (_) {
            /* no Font Loading API: the comment swaps, as it always did */
        }

        // 8. Register service worker after app is mounted
        if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
            import('./registerServiceWorker');
        }

        if (process.env.NODE_ENV === 'development') {
            console.log("Working in development mode");
        }

    } catch (error) {
        console.error('Failed to initialize app:', error);

        // Fatal bootstrap failure. There's no Vue, no i18n and no guaranteed assets
        // here (the bundle itself may have failed), so this screen is fully
        // self-contained and inline — but it must still look like Bronze, be
        // localized, and give a kitchen tablet (no browser chrome to refresh) a way out.
        const dict = {
            es: {
                title: 'No se pudo iniciar la aplicación',
                body: 'Ha ocurrido un error al arrancar. Pulsa el botón para reintentarlo.',
                retry: 'Reintentar',
                details: 'Detalles técnicos',
            },
            en: {
                title: 'The app couldn’t start',
                body: 'Something went wrong while starting. Tap the button to try again.',
                retry: 'Retry',
                details: 'Technical details',
            },
            fr: {
                title: 'L’application n’a pas pu démarrer',
                body: 'Une erreur est survenue au démarrage. Appuie sur le bouton pour réessayer.',
                retry: 'Réessayer',
                details: 'Détails techniques',
            },
            pt: {
                title: 'Não foi possível iniciar a aplicação',
                body: 'Ocorreu um erro ao arrancar. Toca no botão para tentar novamente.',
                retry: 'Tentar novamente',
                details: 'Detalhes técnicos',
            },
        };
        const lang = (typeof navigator !== 'undefined' && navigator.language ? navigator.language : '').slice(0, 2).toLowerCase();
        const t = dict[lang] || dict.en;
        // Escape everything interpolated into innerHTML (error text is untrusted).
        const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
        let deviceId = '';
        try { deviceId = localStorage.getItem('device_id') || ''; } catch (_) { /* ignore */ }
        const techLines = [
            error && error.message ? error.message : String(error),
            deviceId ? `device: ${deviceId}` : '',
            `v${packageInfo.version} (${packageInfo.versionInternal})`,
        ].filter(Boolean).join('\n');

        // Best-effort telemetry — PostHog may not be initialized this early, so guard it.
        try { posthog.capture('app_fatal_init_error', { message: error && error.message, appVersion: packageInfo.versionInternal }); } catch (_) { /* ignore */ }

        if (typeof document !== 'undefined') {
            try { document.documentElement.setAttribute('lang', dict[lang] ? lang : 'en'); } catch (_) { /* ignore */ }
            document.body.style.margin = '0';
            document.body.style.background = '#0C1117';
            // font-family is declared on the container AND inherited by every child
            // via `.bz-fatal *` — the lesson from the old screen is that a global
            // element rule (e.g. on h1) beats inline inheritance and reverts to serif.
            document.body.innerHTML = `
                <div class="bz-fatal">
                    <style>
                        .bz-fatal { position: fixed; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center;
                            padding: 24px; box-sizing: border-box; background: #0C1117; text-align: center;
                            font-family: 'DM Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; }
                        .bz-fatal * { font-family: inherit; }
                        .bz-fatal .bz-wrap { width: 100%; max-width: 420px; display: flex; flex-direction: column; align-items: center; }
                        .bz-fatal .bz-word { font-size: 2rem; font-weight: 700; color: #F8FAFC; letter-spacing: 0.01em; margin-bottom: 32px; }
                        .bz-fatal .bz-icon { margin-bottom: 20px; }
                        .bz-fatal .bz-title { font-size: 1.375rem; font-weight: 600; color: #F8FAFC; line-height: 1.3; margin: 0 0 12px; }
                        .bz-fatal .bz-body { font-size: 1.0625rem; font-weight: 400; color: #CBD5E1; line-height: 1.45; margin: 0 0 28px; }
                        .bz-fatal .bz-retry { width: 100%; height: 56px; border: 0; border-radius: 10px; background: #60A5FA; color: #0C1117;
                            font-size: 1rem; font-weight: 600; cursor: pointer; }
                        .bz-fatal .bz-details { margin-top: 24px; width: 100%; color: #94A3B8; font-size: 0.875rem; }
                        .bz-fatal .bz-details summary { cursor: pointer; }
                        .bz-fatal .bz-details pre { text-align: left; white-space: pre-wrap; word-break: break-word; margin: 10px 0 0;
                            background: #1E293B; border-radius: 10px; padding: 12px; color: #94A3B8; font-size: 0.8125rem; }
                    </style>
                    <div class="bz-wrap">
                        <div class="bz-word">Bronze</div>
                        <svg class="bz-icon" width="44" height="44" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M12 3.8 L22 20.2 L2 20.2 Z" stroke="#FBBF24" stroke-width="1.7" stroke-linejoin="round"/>
                            <line x1="12" y1="10" x2="12" y2="14.2" stroke="#FBBF24" stroke-width="1.7" stroke-linecap="round"/>
                            <circle cx="12" cy="17" r="0.9" fill="#FBBF24"/>
                        </svg>
                        <h1 class="bz-title">${esc(t.title)}</h1>
                        <p class="bz-body">${esc(t.body)}</p>
                        <button class="bz-retry" onclick="location.reload()">${esc(t.retry)}</button>
                        <details class="bz-details">
                            <summary>${esc(t.details)}</summary>
                            <pre>${esc(techLines)}</pre>
                        </details>
                    </div>
                </div>
            `;
        }
    }
}

// Start the initialization
initializeApp();
